import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, Grid, Card, Badge,
} from '../styles/v2.styled';
import useLMSAuth, { isInstructor, isAdmin } from '../hooks/useLMSAuth';
import devopsCloudThumb from '../../image/courses/course-devops-cloud.png';
import nodeBackendThumb from '../../image/courses/course-node-backend.png';
import reactTypescriptThumb from '../../image/courses/course-react-typescript.png';
import aiEngineeringThumb from '../../image/courses/course-ai-engineering.png';
import dataEngineeringThumb from '../../image/courses/course-data-engineering.png';
import web3ContractsThumb from '../../image/courses/course-web3-contracts.png';

// 등록된 강의가 없을 때 가짜 과정을 지어내던 목록이 여기 있었다. 지우면
// 되살아나고, 실제 강의가 생겨도 6개를 맞추려 가짜로 채워서 어느 것이
// 진짜인지 구분할 수 없었다. 남기는 것은 썸네일 순환용 이미지뿐이다.
const FALLBACK_THUMBS = [
  devopsCloudThumb, nodeBackendThumb, reactTypescriptThumb,
  aiEngineeringThumb, dataEngineeringThumb, web3ContractsThumb,
];

const THUMB_BY_CATEGORY = {
  DevOps: devopsCloudThumb,
  Backend: nodeBackendThumb,
  Frontend: reactTypescriptThumb,
  AI: aiEngineeringThumb,
  Data: dataEngineeringThumb,
  Web3: web3ContractsThumb,
};

const resolveCourseThumb = (course, index) => {
  if (course.thumbnail_url) return course.thumbnail_url;
  if (THUMB_BY_CATEGORY[course.category]) return THUMB_BY_CATEGORY[course.category];
  if (/node|backend|백엔드/i.test(course.title || '')) return nodeBackendThumb;
  if (/react|frontend|프론트/i.test(course.title || '')) return reactTypescriptThumb;
  if (/devops|cloud|클라우드/i.test(course.title || '')) return devopsCloudThumb;
  return FALLBACK_THUMBS[index % FALLBACK_THUMBS.length];
};

const normalizeCourses = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((course, index) => ({
    ...course,
    thumbnail_url: resolveCourseThumb(course, index),
  }));
};

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;
const Hero = styled.section`
  padding: 80px 0 40px;
  background: linear-gradient(135deg, #002d33, #0096c7);
  color: #fff;
  text-align: center;
`;
const HeroTitle = styled.h1`font-size: 2.4rem; margin: 0 0 12px; font-weight: 800;`;
const HeroSub = styled.p`font-size: 1.05rem; opacity: 0.85;`;

const Thumb = styled.div`
  width: 100%;
  height: 180px;
  background: ${({ $url, theme }) => ($url ? `url(${$url}) center/cover no-repeat` : `linear-gradient(135deg, ${theme.colors.primary[300]}, ${theme.colors.primary[600]})`)};
  position: relative;
`;
const Body = styled.div`padding: 18px 20px 22px;`;
const CourseTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.3;
`;
const Meta = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.neutral[500]};
  margin-bottom: 8px;
`;
const Desc = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.neutral[600]};
  line-height: 1.55;
  margin: 8px 0;
  min-height: 50px;
`;
const Row = styled.div`display: flex; gap: 8px; margin-top: 12px;`;
const EmptyState = styled.div`
  padding: 48px 24px; text-align: center; font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.neutral[500]};
  border: 1px dashed ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 12px;
`;

const SmallBtn = styled.button`
  padding: 6px 12px; font-size: 0.78rem; border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  background: ${({ $danger }) => $danger ? '#fee2e2' : '#fff'};
  color: ${({ $danger }) => $danger ? '#b91c1c' : '#374151'};
  cursor: pointer;
`;
const PrimaryBtn = styled.button`
  padding: 10px 20px; font-size: 0.95rem; font-weight: 600; border-radius: 8px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary[500]}, ${({ theme }) => theme.colors.primary[700]});
  color: #fff; border: none; cursor: pointer;
`;
const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
`;
const ModalBox = styled.div`
  background: #fff; border-radius: 14px; padding: 28px; width: 90%; max-width: 560px;
  max-height: 90vh; overflow-y: auto;
`;
const Field = styled.div`margin-bottom: 14px;`;
const Label = styled.label`display:block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px; color: #374151;`;
const Input = styled.input`width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box;`;
const TextArea = styled.textarea`width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box; min-height: 80px;`;
const Select = styled.select`width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box;`;

const empty = {
  title: '', instructor_id: '', category: '', description: '',
  thumbnail_url: '', video_url: '', capacity: 30, price: 0,
  start_date: '', end_date: '', status: 'open',
};

export default function CoursesPage() {
  const { role, login } = useLMSAuth();
  const [list, setList] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const [coursesRes, instructorsRes] = await Promise.all([
        fetch('/lms/courses'),
        fetch('/lms/instructors'),
      ]);
      if (coursesRes.ok) {
        setList(normalizeCourses(await coursesRes.json()));
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
      if (instructorsRes.ok) {
        setInstructors(await instructorsRes.json());
      }
    } catch {
      // 못 불러왔을 때도 지어내지 않는다. 비어 있는 것과 못 읽은 것은
      // 다른 상황이고, 화면이 그 둘을 같게 보이면 장애를 놓친다.
      setList([]);
      setLoadFailed(true);
      setInstructors([]);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setEdit({
      ...empty, ...c,
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      end_date: c.end_date ? c.end_date.slice(0, 10) : '',
    });
    setEditId(c.id);
    setOpen(true);
  };
  const save = async () => {
    if (!edit.title) { alert('과정명은 필수입니다.'); return; }
    const url = editId ? `/lms/courses/${editId}` : '/lms/courses';
    const r = await fetch(url, {
      method: editId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...edit,
        instructor_id: edit.instructor_id ? Number(edit.instructor_id) : null,
        capacity: Number(edit.capacity) || 30,
        price: Number(edit.price) || 0,
      }),
    });
    if (!r.ok) { alert('저장 실패: ' + (await r.text())); return; }
    setOpen(false);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    const r = await fetch(`/lms/courses/${id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) load(); else alert('삭제 실패');
  };

  return (
    <PageWrapper>
      <Hero>
        <HeroTitle>수업 과정</HeroTitle>
        <HeroSub>실무 중심의 체계적인 LMS 강의 과정</HeroSub>
      </Hero>

      <Section>
        <Inner>
          <SectionHeader>
            <Divider $center />
            <SectionTitle>전체 과정</SectionTitle>
            <SectionSubtitle>
              {loadFailed
                ? '과정 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
                : `총 ${list.length}개의 과정이 등록되어 있습니다`}
            </SectionSubtitle>
            {isInstructor(role) && (
              <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'center' }}>
                {/* 등록은 스튜디오로 보낸다. 모달은 제목·정원만 받아서
                    커리큘럼을 짤 수 없었고, 그래서 등록된 과정이 껍데기로 남았다. */}
                <Link to="/CourseStudio"><PrimaryBtn>+ 강의 등록</PrimaryBtn></Link>
              </div>
            )}
          </SectionHeader>

          {!loadFailed && list.length === 0 && (
            <EmptyState>
              아직 등록된 강의가 없습니다.
              {isInstructor(role)
                ? ' 스튜디오에서 첫 강의를 만들어 보세요.'
                : ' 곧 새로운 과정이 열립니다.'}
            </EmptyState>
          )}

          <Grid $cols={3} $colsTablet={2} $colsMobile={1}>
            {list.map((c) => (
              <Card key={c.id}>
                <Thumb $url={c.thumbnail_url} data-testid="course-thumbnail" />
                <Body>
                  <Badge $variant={c.status === 'open' ? 'success' : 'default'}>
                    {c.status === 'open' ? '모집중' : c.status}
                  </Badge>
                  {c.category && <Badge style={{ marginLeft: 4 }}>{c.category}</Badge>}
                  <CourseTitle style={{ marginTop: 8 }}>{c.title}</CourseTitle>
                  <Meta>강사: {c.instructor_name || '미정'} · 정원 {c.enrolled_count || 0}/{c.capacity}</Meta>
                  <Desc>{c.description}</Desc>
                  <Row>
                    {login && (
                      <Link to={`/Enroll?course=${c.id}`} style={{ flex: 1 }}>
                        <SmallBtn style={{ width: '100%' }}>수강신청</SmallBtn>
                      </Link>
                    )}
                    {isInstructor(role) && typeof c.id === 'number' && (
                      <Link to={`/CourseStudio/${c.id}`}><SmallBtn>강의 편집</SmallBtn></Link>
                    )}
                    {isInstructor(role) && <SmallBtn onClick={() => openEdit(c)}>정보 수정</SmallBtn>}
                    {isAdmin(role) && <SmallBtn $danger onClick={() => remove(c.id)}>삭제</SmallBtn>}
                  </Row>
                </Body>
              </Card>
            ))}
          </Grid>
        </Inner>
      </Section>

      {open && (
        <Modal onClick={() => setOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 18px' }}>{editId ? '과정 수정' : '과정 등록'}</h3>
            <Field><Label>과정명</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field>
            <Field><Label>담당 강사</Label>
              <Select value={edit.instructor_id || ''} onChange={(e) => setEdit({ ...edit, instructor_id: e.target.value })}>
                <option value="">선택</option>
                {instructors.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.title})</option>)}
              </Select>
            </Field>
            <Field><Label>카테고리</Label><Input value={edit.category || ''} onChange={(e) => setEdit({ ...edit, category: e.target.value })} /></Field>
            <Field><Label>설명</Label><TextArea value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
            <Field><Label>썸네일 URL</Label><Input value={edit.thumbnail_url || ''} onChange={(e) => setEdit({ ...edit, thumbnail_url: e.target.value })} /></Field>
            <Field><Label>비디오 URL (강의 영상)</Label><Input value={edit.video_url || ''} onChange={(e) => setEdit({ ...edit, video_url: e.target.value })} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field><Label>정원</Label><Input type="number" value={edit.capacity} onChange={(e) => setEdit({ ...edit, capacity: e.target.value })} /></Field>
              <Field><Label>가격</Label><Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} /></Field>
              <Field><Label>시작일</Label><Input type="date" value={edit.start_date || ''} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} /></Field>
              <Field><Label>종료일</Label><Input type="date" value={edit.end_date || ''} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} /></Field>
            </div>
            <Field><Label>상태</Label>
              <Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                <option value="open">모집중</option>
                <option value="closed">모집마감</option>
                <option value="ended">종료</option>
              </Select>
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <SmallBtn onClick={() => setOpen(false)}>취소</SmallBtn>
              <PrimaryBtn onClick={save}>저장</PrimaryBtn>
            </div>
          </ModalBox>
        </Modal>
      )}
    </PageWrapper>
  );
}
